<template>
  <b-container :class="embedded ? 'px-0' : 'container-rda'">
    <b-row v-if="!embedded">
      <b-col>
        <Breadcrumb :breadcrumb="breadcrumb" :showBackButton="false" />
      </b-col>
    </b-row>
    <b-row class="mb-5" v-if="!embedded">
      <b-col>
        <h2 class="purple bold">Relatório de Frequência</h2>
      </b-col>

      <b-col class="text-right">
        <b-button class="btn small blue" @click="() => $router.go(-1)"
          >Voltar</b-button
        >
      </b-col>
    </b-row>
    <b-alert :show="!!summaryError" variant="danger">
      {{ summaryError }}
      <b-button size="sm" variant="outline-danger" @click="loadReport"
        >Tentar novamente</b-button
      >
    </b-alert>
    <div v-if="loading" class="text-center mb-3">
      <b-spinner variant="success" label="Carregando frequência..."></b-spinner>
    </div>
    <b-row class="mb-4" aria-live="polite">
      <b-col md="4" class="mb-3">
        <div class="attendance-card">
          <span class="attendance-card__icon">
            <b-icon icon="calendar-check" aria-hidden="true"></b-icon>
          </span>
          <div>
            <p class="attendance-card__label">Frequência</p>
            <p class="attendance-card__value">
              {{
                attendances.frequency != null
                  ? attendances.frequency + "%"
                  : "—"
              }}
            </p>
          </div>
        </div>
      </b-col>
      <b-col md="4" class="mb-3">
        <div class="attendance-card">
          <span class="attendance-card__icon">
            <b-icon icon="clock-history" aria-hidden="true"></b-icon>
          </span>
          <div>
            <p class="attendance-card__label">Total de horas ausentes</p>
            <p class="attendance-card__value">
              {{
                attendances.time_absences != null
                  ? attendances.time_absences || "0h"
                  : "—"
              }}
            </p>
          </div>
        </div>
      </b-col>
      <b-col md="4" class="mb-3">
        <div class="attendance-card">
          <span
            class="attendance-card__icon"
            :class="`attendance-card__icon--${situationStatus.modifier}`"
          >
            <b-icon :icon="situationStatus.icon" aria-hidden="true"></b-icon>
          </span>
          <div>
            <p class="attendance-card__label">Situação</p>
            <p
              class="attendance-card__value"
              :class="{ 'attendance-card__value--compact': embedded }"
            >
              {{ attendances.situation || "—" }}
            </p>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row
      class="mb-5 attendance-advice"
      v-if="
        student &&
        student.Cycle &&
        student.Cycle.absence_limit_perc_responsible &&
        !isStudent()
      "
    >
      <b-col>
        <p>
          Ao atingir 30% do limite permitido de faltas ({{
            student.Cycle.absence_limit_perc_responsible
          }}
          horas), a Escola tem obrigação legal de comunicar ao Conselho Tutelar
        </p>
      </b-col>
    </b-row>
    <b-alert :show="!!recordsError" variant="danger">{{
      recordsError
    }}</b-alert>
    <b-row v-if="!recordsError">
      <b-col>
        <DataTable
          id="attendancesList"
          :items="attendancesList"
          :fields="attendanceFields"
          :busy="recordsLoading"
          empty-text="Nenhum registro encontrado"
        >
          <template #cell(type)="{ item }">
            <span class="attendance" :class="item.type">
              {{ getAttendanceTypeLabel(item.type) }}
            </span>
          </template>
        </DataTable>

        <b-row class="align-center" v-if="count > perPage">
          <b-pagination
            v-model="page"
            :per-page="perPage"
            :total-rows="count"
            @change="changePage"
            aria-controls="attendancesList"
            last-text
            first-text
          >
            <template #prev-text>
              <LeftArrowIcon class="left" />
            </template>
            <template #next-text>
              <LeftArrowIcon />
            </template>
          </b-pagination>
        </b-row>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import httpRda from "@/http-rda";
import { mapGetters } from "vuex";
import DataTable from "@/components/table/DataTable.vue";
import LeftArrowIcon from "@/components/svg/LeftArrowIcon.vue";

const SITUATION_STATUS = {
  regular: { modifier: "regular", icon: "check-circle" },
  within_limit: { modifier: "warning", icon: "exclamation-circle" },
  failure_risk: { modifier: "danger", icon: "exclamation-circle-fill" },
  failed: { modifier: "danger", icon: "x-circle-fill" },
};

const UNKNOWN_SITUATION_STATUS = { modifier: "unknown", icon: "dash-circle" };

export default {
  name: "StudentAttendanceReport",
  components: { DataTable, LeftArrowIcon },
  props: {
    studentId: { type: [Number, String], default: null },
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      student: null,
      page: 1,
      count: 0,
      perPage: 20,
      attendancesList: [],
      attendances: {
        frequency: null,
        time_absences: null,
        situation: null,
        situation_key: null,
      },
      loading: false,
      recordsLoading: false,
      summaryError: null,
      recordsError: null,
      attendanceFields: [
        { key: "classroom_designation", label: "Turma" },
        { key: "type", label: "Evento" },
        {
          key: "period",
          label: "Tipo",
          formatter: (period) =>
            period === "regular" ? "Horário regular" : "Contraturno",
        },
        { key: "date", label: "Data e Hora" },
      ].map((field) => ({
        ...field,
        thClass: "text-center",
        tdClass: "text-center",
      })),
      breadcrumb: [
        { text: "Início", href: "/" },
        {
          text: "Faltas e Presenças",
          href: "/faltas-e-presencas",
          active: true,
        },
      ],
    };
  },
  computed: {
    ...mapGetters({ user: "auth/getAuthData" }),
    effectiveStudentId() {
      return (
        this.studentId || this.$route.params.student_id || this.user.student_id
      );
    },
    situationStatus() {
      return (
        SITUATION_STATUS[this.attendances.situation_key] ||
        UNKNOWN_SITUATION_STATUS
      );
    },
  },
  watch: {
    effectiveStudentId() {
      this.page = 1;
      this.loadReport();
    },
  },
  methods: {
    getAttendanceTypeLabel(type) {
      switch (type) {
        case "absence":
          return "Falta";
        case "presence":
          return "Presença";
        case "late":
          return "Chegada Tardia";
        case "antecipated":
          return "Saída Antecipada";
        case "regular_leave":
          return "Saída Regular";
        default:
          return "";
      }
    },
    async loadReport() {
      const studentId = this.effectiveStudentId;
      if (!studentId) {
        this.summaryError = "Não foi possível identificar o aluno.";
        return;
      }

      this.loading = true;
      this.student = null;
      this.attendances = {
        frequency: null,
        time_absences: null,
        situation: null,
        situation_key: null,
      };
      this.summaryError = null;
      this.recordsError = null;
      this.attendancesList = [];
      this.count = 0;

      try {
        await Promise.all([
          this.getSummary(studentId),
          this.getStudent(studentId),
          this.getAttendances(studentId),
        ]);
      } finally {
        this.loading = false;
      }
    },
    async getSummary(studentId) {
      try {
        const response = await httpRda.get(
          `/students/${studentId}/attendance-summary`
        );
        this.attendances = response.data;
      } catch (error) {
        this.summaryError = "Não foi possível carregar a frequência do aluno.";
      }
    },
    async getStudent(studentId) {
      try {
        const response = await httpRda.get(
          `/students/${studentId}?include=cycle`
        );
        this.student = response.data.student;
      } catch (error) {
        this.summaryError =
          this.summaryError || "Não foi possível carregar os dados do aluno.";
      }
    },
    async getAttendances(studentId = this.effectiveStudentId) {
      this.recordsError = null;
      this.recordsLoading = true;
      try {
        const response = await httpRda.get("/attendances", {
          params: {
            student_id: studentId,
            current_cycle: true,
            page: this.page,
            limit: this.perPage,
          },
        });
        this.attendancesList = response.data.attendances;
        this.count = response.data.count;
      } catch (error) {
        this.recordsError =
          "Não foi possível carregar os registros de frequência.";
      }
      this.recordsLoading = false;
    },
    changePage(page) {
      this.page = page;
      this.getAttendances();
      if (!this.embedded) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    isStudent() {
      return this.user.role.includes("student");
    },
  },
  created() {
    this.loadReport();
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/variables.scss";

.home {
  height: 100%;
}

.attendance-card {
  background-color: $white;
  border: 1px solid #f2f2f2;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 16px;
  height: 100%;
  padding: 20px;

  &__icon {
    align-items: center;
    color: $purple;
    display: flex;
    flex-shrink: 0;
    font-size: 1.4rem;
    height: 48px;
    justify-content: center;
    width: 48px;

    &--regular {
      color: darken($green1, 15%);
    }

    &--warning {
      color: darken($orange2, 15%);
    }

    &--danger {
      color: darken($red, 15%);
    }

    &--unknown {
      color: $grey4;
    }
  }

  &__label {
    color: #666666;
    font-size: 14px;
    line-height: 1.25;
    margin: 0 0 4px;
  }

  &__value {
    color: #333333;
    font-size: 1.25rem;
    font-weight: 700;
    line-height: 1.2;
    margin: 0;
    overflow-wrap: anywhere;

    &--compact {
      font-size: 1.125rem;
    }
  }
}

.attendance {
  border-radius: 5px;
  display: block;
  color: $white;
  font-weight: 700;
  line-height: 28px;
  margin: 0 auto;
  max-width: 200px;
  text-align: center;

  &.late {
    background-color: $orange;
  }

  &.absence {
    background-color: $red;
  }

  &.presence {
    background-color: $green2;
  }

  &.antecipated {
    background-color: $purple;
  }

  &.regular_leave {
    background-color: $yellow;
  }
}

.attendance-advice {
  width: 100% !important;
  background-color: #ef6549;
  border-radius: 5px;
  padding: 10px 10px;

  display: flex;
  align-self: center;
  justify-self: center !important;
  text-align: center;

  p {
    color: #fff;
    margin: 0;
    font-weight: 600;
    font-size: 16px;
    letter-spacing: 1px;
  }
}
</style>
